import {
  ClientTeamBulkRequest,
  ClientTeamBulkResult,
  ClientTeamClients,
  ClientTeamLookup,
  ClientTeamView,
} from "../features/clientTeam/clientTeam.types";
import { contactsSlice } from "./ContactsSlice";

/**
 * Client team (backend `api/client-team`, Mg-231): each client's Client owner
 * and Client manager. Injected into the contacts API (no new store slice), so
 * saving also refreshes the Contacts list ("Contacts" tag).
 */
export const clientTeamApi = contactsSlice
  .enhanceEndpoints({ addTagTypes: ["ClientTeam"] })
  .injectEndpoints({
    endpoints: (build) => ({
      getClientTeam: build.query<ClientTeamView, { customerId: string }>({
        query: ({ customerId }) => ({ url: `api/client-team/${customerId}` }),
        providesTags: (_r, _e, { customerId }) => [
          { type: "ClientTeam", id: customerId },
        ],
      }),
      setClientTeam: build.mutation<
        ClientTeamView,
        {
          customerId: string;
          ownerUserId: string | null;
          managerUserId: string | null;
        }
      >({
        query: ({ customerId, ...body }) => ({
          url: `api/client-team/${customerId}`,
          method: "PUT",
          body,
        }),
        invalidatesTags: (_r, _e, { customerId }) => [
          { type: "ClientTeam", id: customerId },
          { type: "ClientTeam", id: "LIST" },
          "Contacts",
        ],
      }),
      bulkSetClientTeam: build.mutation<
        ClientTeamBulkResult,
        ClientTeamBulkRequest
      >({
        query: (body) => ({
          url: "api/client-team/bulk",
          method: "POST",
          body,
        }),
        invalidatesTags: ["ClientTeam", "Contacts"],
      }),
      lookupClientTeams: build.query<
        ClientTeamLookup,
        { orgId: string; customerIds: string[] }
      >({
        query: (body) => ({
          url: "api/client-team/lookup",
          method: "POST",
          body,
        }),
        providesTags: [{ type: "ClientTeam", id: "LIST" }],
      }),
      getClientTeamClients: build.query<
        ClientTeamClients,
        { orgId: string; ownerIds: string[]; managerIds: string[] }
      >({
        query: ({ orgId, ownerIds, managerIds }) => {
          const params = new URLSearchParams({ orgId });
          ownerIds.forEach((id) => params.append("ownerIds", id));
          managerIds.forEach((id) => params.append("managerIds", id));
          return { url: `api/client-team/clients?${params.toString()}` };
        },
        providesTags: [{ type: "ClientTeam", id: "LIST" }],
      }),
    }),
  });

export const {
  useGetClientTeamQuery,
  useSetClientTeamMutation,
  useBulkSetClientTeamMutation,
  useLookupClientTeamsQuery,
  useGetClientTeamClientsQuery,
} = clientTeamApi;
